import { useState, useEffect, useRef, useCallback } from 'react'
import styles from './DailyTips.module.css'

interface Tip {
  emoji: string
  title: string
  text: string
}

const ALL_TIPS: Tip[] = [
  { emoji: '', title: 'Movement before focus', text: 'A 15-minute movement break before homework helps your child sit still and focus for 20-30 minutes.' },
  { emoji: '', title: 'Match, then calm', text: 'When your child melts down, match their energy first — then slowly bring it down. Jumping straight to "calm down" doesn\'t work.' },
  { emoji: '', title: 'Backward chaining', text: 'Dressing battles? Let your child do just the last step (pull shirt down). They finish with success every time.' },
  { emoji: '', title: '15-20 exposures', text: 'Children need 15-20 positive exposures to a new food before they may accept it. Every interaction counts — even just looking.' },
  { emoji: '', title: 'Brain still growing', text: 'Your child\'s brain won\'t develop full self-control until their mid-20s. What looks like defiance is often a brain that\'s still under construction.' },
  { emoji: '', title: 'Deep pressure calms', text: 'Firm hugs, heavy blankets, or a tight "burrito roll" in a bedsheet calms the nervous system in under 2 minutes.' },
  { emoji: '', title: 'Routine over timing', text: 'A consistent bedtime routine matters more than the exact bedtime. Same steps, same order, every night.' },
  { emoji: '', title: 'Malish magic', text: 'Oil massage (malish) before bed activates the parasympathetic nervous system. 5 minutes of firm strokes on arms and legs works wonders.' },
  { emoji: '', title: 'Kitchen helper', text: 'Let your child help in the kitchen — washing tomatoes, tearing coriander, stirring dal. It builds food comfort without any pressure to eat.' },
  { emoji: '', title: 'Bear walks', text: 'Bear walks (hands and feet, bottom up) build the exact muscles your child needs for writing, sitting upright, and staying focused.' },
  { emoji: '', title: 'Night-before prep', text: 'The secret to calm mornings starts the night before. Clothes out, bag packed, breakfast prepped — eliminates 50% of morning chaos.' },
  { emoji: '', title: 'Transitions are hard', text: 'Every morning has 8-10 transitions. Give advance warnings: "2 more minutes, then we brush teeth." Sudden switches cause meltdowns.' },
  { emoji: '', title: 'Name it to tame it', text: 'When you label your child\'s emotion — "you look frustrated" — it activates their thinking brain and begins to calm the storm.' },
  { emoji: '', title: 'Balloon belly', text: 'Place a toy on your child\'s belly. Breathe in: toy goes up. Breathe out: toy goes down. 5 rounds and they\'re calmer.' },
  { emoji: '', title: 'Sensory seeking', text: 'If your child jumps on sofas and crashes into things, they\'re not naughty — their body needs more movement input. Channel it, don\'t fight it.' },
  { emoji: '', title: 'Tags and seams', text: 'If clothes cause meltdowns, it\'s not drama — tactile sensitivity affects up to 15% of children. Cut every tag, choose soft cotton.' },
  { emoji: '', title: 'Visual schedules', text: 'A picture-based checklist helps your child follow routines independently. Their brain can\'t hold 12 steps — give it an external helper.' },
  { emoji: '', title: 'Food chaining', text: 'Start with a food they eat and change one thing: plain rice to curd rice, roti to roti with ghee. Small steps build big acceptance.' },
  { emoji: '', title: 'Comfort object', text: 'A familiar-smelling object at bedtime — your dupatta, a favourite toy — activates feelings of safety and helps your child settle.' },
  { emoji: '', title: 'Flipped lid', text: 'During a meltdown, your child\'s thinking brain goes offline. They literally cannot hear logic or reason. Wait for calm, then talk.' },
  { emoji: '', title: 'Make it a game', text: 'Use a timer for morning routines: "Can you brush before the sand runs out?" The timer is the boss, not you.' },
  { emoji: '', title: 'Repair after storms', text: 'After a meltdown, reconnect with warmth — a hug, a kind word. Repair teaches your child that relationships survive hard moments.' },
  { emoji: '', title: 'Screen-free hour', text: 'No screens 1 hour before bed. Blue light suppresses melatonin for up to 2 hours, making it much harder to fall asleep.' },
  { emoji: '', title: 'Calm corner', text: 'Create a small space with a cushion, soft toy, and breathing card. Your child can go there to self-regulate — it\'s not a punishment corner.' },
  { emoji: '', title: 'Co-regulation', text: 'Children under 6-7 can\'t regulate emotions alone. YOUR calm is their calm. Regulate yourself first, then help them.' },
  { emoji: '', title: 'No pressure meals', text: 'Never say "just try one bite." Pressure makes picky eating worse. Serve food, eat together, and trust your child\'s pace.' },
  { emoji: '', title: 'Elephant walks', text: 'Slow, heavy steps with arms swinging like a trunk. This provides deep calming input — perfect before homework or meals.' },
  { emoji: '', title: 'Spice painting', text: 'Mix haldi with water for yellow paint, use coriander as a brush. Food art builds comfort with ingredients before they reach the plate.' },
  { emoji: '', title: 'Family scripts', text: 'When Dadi says "just be strict," try: "The doctor says staying calm reduces meltdowns faster than scolding." Frame it as expert advice.' },
  { emoji: '', title: 'Flamingo balance', text: 'Standing on one leg with eyes closed builds sustained attention and body awareness. Make it a daily challenge!' },
]

function getDailyTips(count: number): Tip[] {
  const today = new Date()
  const dayOfYear = Math.floor(
    (today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / 86400000
  )
  const shuffled = [...ALL_TIPS]
  // Deterministic shuffle based on day
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = (dayOfYear * 7 + i * 13) % (i + 1)
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled.slice(0, count)
}

export default function DailyTips() {
  const [activeIndex, setActiveIndex] = useState(0)
  const scrollRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<ReturnType<typeof setInterval>>()
  const tips = useRef(getDailyTips(5)).current

  const scrollTo = useCallback((index: number) => {
    const el = scrollRef.current
    if (!el) return
    const cardWidth = el.scrollWidth / tips.length
    el.scrollTo({ left: cardWidth * index, behavior: 'smooth' })
  }, [tips.length])

  // Auto-advance
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActiveIndex(prev => {
        const next = (prev + 1) % tips.length
        scrollTo(next)
        return next
      })
    }, 6000)
    return () => clearInterval(timerRef.current)
  }, [tips.length, scrollTo])

  // Track scroll position
  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const cardWidth = el.scrollWidth / tips.length
    const index = Math.round(el.scrollLeft / cardWidth)
    if (index !== activeIndex) {
      setActiveIndex(index)
      // Reset auto-advance timer on manual scroll
      clearInterval(timerRef.current)
      timerRef.current = setInterval(() => {
        setActiveIndex(prev => {
          const next = (prev + 1) % tips.length
          scrollTo(next)
          return next
        })
      }, 6000)
    }
  }

  return (
    <div className={styles.container}>
      <p className={styles.label}>Daily tip</p>
      <div className={styles.scroll} ref={scrollRef} onScroll={handleScroll}>
        {tips.map((tip, i) => (
          <div key={i} className={styles.card}>
            <span className={styles.emoji}>{tip.emoji}</span>
            <div className={styles.textWrap}>
              <p className={styles.tipTitle}>{tip.title}</p>
              <p className={styles.tipText}>{tip.text}</p>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.dots}>
        {tips.map((_, i) => (
          <div
            key={i}
            className={`${styles.dot} ${i === activeIndex ? styles.dotActive : ''}`}
          />
        ))}
      </div>
    </div>
  )
}
